/**
 * What a person can see. Mirrors rt_common.authz (roles → permissions) so navigation renders
 * instantly from the session; the server's own list from GET /v1/me replaces it once loaded, and
 * every endpoint enforces it regardless.
 */
import type { Role } from '@robotensor/api'

const CONTRIBUTOR = ['missions:view', 'contrib:own', 'rewards:own', 'devices:own', 'library:read', 'skills:read', 'sim:teleop', 'realdata:capture', 'realtosim:scan', 'notifications:own']
const REVIEWER = ['review:realdata', 'review:realtosim', 'review:sim']
const ANNOTATOR = ['annotate']
const RESEARCHER = ['library:read_internal', 'sim:author', 'sim:evaluate', 'realdata:datasets', 'realtosim:author', 'objectives:propose', 'contrib:view_all']
const OPERATOR = [...REVIEWER, ...ANNOTATOR, ...RESEARCHER, 'missions:manage', 'objectives:manage', 'library:publish', 'skills:manage', 'users:view', 'rewards:view', 'sim:operate', 'realdata:operate', 'realtosim:operate', 'devices:manage', 'audit:view']
const AUDITOR = ['audit:view', 'rewards:view', 'users:view', 'contrib:view_all', 'library:read_internal']
const ADMIN = [...OPERATOR, ...AUDITOR, 'users:manage', 'orgs:manage', 'rewards:configure', 'rewards:approve', 'library:manage', 'system:manage', 'company:manage']

const ROLE_PERMISSIONS: Record<Role, readonly string[]> = {
  contributor: CONTRIBUTOR,
  reviewer: [...CONTRIBUTOR, ...REVIEWER],
  annotator: [...CONTRIBUTOR, ...ANNOTATOR],
  researcher: [...CONTRIBUTOR, ...RESEARCHER],
  operator: [...CONTRIBUTOR, ...OPERATOR],
  auditor: [...CONTRIBUTOR, ...AUDITOR],
  // the team level's pages and permissions come from the access token (`grt`, chosen by an admin): see tokenGrants
  team: CONTRIBUTOR,
  admin: [...CONTRIBUTOR, ...ADMIN],
}

export function permissionsFor(roles: readonly string[], grants: readonly string[] = []): Set<string> {
  const out = new Set<string>(grants)
  for (const r of roles) for (const p of ROLE_PERMISSIONS[r as Role] ?? []) out.add(p)
  return out
}

/** The permissions an admin gave the team level, as the platform put them in this access token (`grt`). The server
 *  enforces them; this only decides what the navigation shows. */
export function tokenGrants(accessToken: string | undefined): string[] {
  try {
    const part = accessToken?.split('.')[1]
    if (!part) return []
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { grt?: unknown }
    return Array.isArray(json.grt) ? json.grt.filter((g): g is string => typeof g === 'string') : []
  } catch {
    return []
  }
}

/** Team members with no other staff role see only the pages an admin turned on for the team (plus Overview). */
export function teamOnly(roles: readonly string[]): boolean {
  return roles.includes('team') && !roles.some((r) => r !== 'team' && r !== 'contributor')
}

const PROFESSIONAL: readonly Role[] = ['reviewer', 'annotator', 'researcher', 'operator', 'auditor', 'team', 'admin']

/** Has any role beyond contributor: sees the professional workspace. */
export function isProfessional(roles: readonly string[]): boolean {
  return roles.some((r) => (PROFESSIONAL as readonly string[]).includes(r))
}

export const ROLE_LABEL: Record<string, string> = {
  contributor: 'Contributor',
  reviewer: 'Reviewer',
  annotator: 'Annotator',
  researcher: 'Researcher',
  operator: 'Operator',
  auditor: 'Auditor',
  team: 'Team',
  admin: 'Admin',
}

export const ROLE_DESCRIPTION: Record<string, string> = {
  contributor: 'Every person. Missions, contributions, rewards, own devices.',
  reviewer: 'Decides contributions in review queues for robot control, recordings and scans.',
  annotator: 'Labels and corrects recordings.',
  researcher: 'Internal library, simulation authoring and evaluation, dataset builds, proposes objectives.',
  operator: 'Runs missions and objectives, publishes to the library, views people and audit.',
  auditor: 'Read-only access to audit, rewards and people for oversight.',
  team: 'Robotensor staff (@robotensor.ai) who see the ops pages an admin chose for the team.',
  admin: 'Manages people, roles, organisations, reward configuration and approvals; chooses what the team sees.',
}

/** The most senior role, for compact labels. */
export function primaryRole(roles: readonly string[]): string {
  const order: Role[] = ['admin', 'operator', 'auditor', 'researcher', 'reviewer', 'annotator', 'contributor']
  return order.find((r) => roles.includes(r)) ?? 'contributor'
}
